import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useSelectedStore, useUpdateTargetSite } from '@web/app/shared';
import axios, { AxiosError } from 'axios';

import { useLoader } from '@codes/loader';
import { ownerQueryKeys, useOwner } from '@codes/owner';
import { projectsKeys } from '@codes/project';
import { toast } from '@codes/ui-kit';
import { isProdEnvironment, useProfile, useWebCoreStore } from '@codes/web-core';
import { useMyWorkspaces, useWorkspace, workspacesKeys } from '@codes/workspace';

export const useWorkspaceService = () => {
    const isProd = isProdEnvironment();
    const navigate = useNavigate();

    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { setIsLoading } = useLoader();
    const updateTargetSite = useUpdateTargetSite();

    const { fetchAndSetOwner } = useOwner();
    const { loadProfile } = useProfile();

    const { setProject, setWorkspace } = useSelectedStore();

    const workspaceFromServerQueryData = useWorkspace('0');

    const myWorkspacesQueryResult = useMyWorkspaces();

    const { profile } = useWebCoreStore();

    const myWorkspaces =
        !!myWorkspacesQueryResult.data && !!myWorkspacesQueryResult.data.data ? myWorkspacesQueryResult.data.data : [];

    const areMyWorkspacesEmpty =
        !!myWorkspacesQueryResult.data &&
        !!myWorkspacesQueryResult.data.data &&
        myWorkspacesQueryResult.data.total === 0;

    const isApprovedWorkspace = useMemo(() => {
        const isDefault = workspaceFromServerQueryData.data?.name === 'default';

        if (isProd && isDefault) {
            return true;
        }

        return !!workspaceFromServerQueryData.data?.approvedAt;
    }, [workspaceFromServerQueryData, isProd]);

    const replaceWorkspace = async (workspaceId: string, siteId: string) => {
        const { workspace: currentWorkspace } = useSelectedStore.getState();
        const isChangingWorkspace = currentWorkspace && currentWorkspace.siteId !== siteId;

        setIsLoading(true);
        try {
            // TODO: refresh하는게 나은지 판단필요
            await updateTargetSite(siteId);
            const owner = await fetchAndSetOwner();
            await loadProfile();
            queryClient.removeQueries({ queryKey: ownerQueryKeys.list({}), exact: false });
            queryClient.removeQueries({ queryKey: projectsKeys.lists(), exact: false });
            queryClient.removeQueries({ queryKey: workspacesKeys.detail('0') });

            setWorkspace(owner?.$workspace || null);
            // Only reset project when actually changing to a different workspace
            if (isChangingWorkspace) {
                setProject(null);
            }

            // Navigate to the new workspace-based URL structure
            navigate(`/w/${workspaceId}`, { replace: true });
        } catch (e) {
            if (axios.isAxiosError(e)) {
                toast({ description: e.response?.data });
            } else {
                toast({ description: t('error') });
            }
            throw e;
        } finally {
            setIsLoading(false);
        }
    };

    const isOwner = useMemo(() => {
        if (!profile) {
            return false;
        }
        const roles = (profile.roles || []) as string[];

        return roles.includes('owner');
    }, [profile]);

    useEffect(() => {
        if (!workspaceFromServerQueryData.error) {
            return;
        }

        const axiosError = workspaceFromServerQueryData.error as AxiosError<string>;
        toast({ description: axiosError.response?.data || 'unknown error' });

        setWorkspace(null);
        setProject(null);

        // refresh(defaultWorkspace?.siteId as string);
    }, [workspaceFromServerQueryData.error]);

    return {
        myWorkspacesQueryResult,
        myWorkspaces,
        areMyWorkspacesEmpty,
        isApprovedWorkspace,
        workspaceFromServer: workspaceFromServerQueryData.data,
        workspaceFromServerQueryData,
        replaceWorkspace,
        isOwner,
    };
};
